import Axios from 'axios'
import {API_TIMEOUT_MS, timeoutFromEnv} from '../../configs/apiTimeout'
import {getAccessToken, getRefreshToken} from '../AuthServices'

const _store = {
    errorSubscribers: [],
}

const _makeRequest = createRequest => async args => {
    const _headers = args.headers ? args.headers : {}

    const defaultHeaders = {}

    args = {
        ...args,
        headers: {
            ...defaultHeaders,
            ..._headers,
        },
    }

    try {
        const {data} = await createRequest(args)

        return data
    } catch (e) {
        throw e
    }
}

const _makeAuthRequest = createRequest => async (args) => {
    const requestHeaders = args.headers ? args.headers : {}
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    let headers = {
        'Authorization': `Bearer ${accessToken}`,
        'cache-control': 'no-cache',
    }

    if (refreshToken) {
        headers['X-Refresh-Token'] = refreshToken
    }

    args = {
        ...args,
        headers: {
            ...headers,
            ...requestHeaders,
        },
    }

    try {
        return await _makeRequest(createRequest)(args)
    } catch (error) {
        const {response} = error

        if (!response || typeof response !== 'object') {
            throw error
        }

        const {status, data} = response
        if (status && !error.__skipErrorSubscribers) {
            _store.errorSubscribers.forEach(subscriber => {
                subscriber({status, data})
            })
        }

        throw error
    }
}

const _makeBasicRequest = createRequest => async (args) => {
    const requestHeaders = args.headers ? args.headers : {}
    const basicAuth = process.env.REACT_APP_BASIC_AUTH_PLF || 'c2VydmljZTpzcjJBbUJaZXUyOGpZOGJ6SjJ4NnE0OHJVVkczZ0VuYw=='

    let headers = {
        'Authorization': `Basic ${basicAuth}`,
        'cache-control': 'no-cache',
    }

    args = {
        ...args,
        headers: {
            ...headers,
            ...requestHeaders,
        },
    }

    try {
        return await _makeRequest(createRequest)(args)
    } catch (error) {
        const {response} = error

        if (!response || typeof response !== 'object') {
            throw error
        }

        const {status, data} = response
        if (status && !error.__skipErrorSubscribers) {
            _store.errorSubscribers.forEach(subscriber => {
                subscriber({status, data})
            })
        }

        throw error
    }
}

const _subscribeErrorRequest = instance => (subscriber = null) => {
    if (!subscriber || typeof subscriber !== 'function') return false
    if (_store.errorSubscribers.indexOf(subscriber) !== -1) return false

    _store.errorSubscribers = [].concat(_store.errorSubscribers, [subscriber])
}

/**
 * @param options
 * @return {{
 * getBaseURL: function,
 * makeAuthRequest: function,
 * makeRequest: function,
 * changeBaseURL: function
 * }}
 */
export default (options = {}) => {
    const baseUrlValidated = options.baseUrl || '/api'
    const headers = Object.assign({}, options.headers)

    /*
     * Trần chờ: `options.timeout` > `REACT_APP_API_TIMEOUT_MS` > 30 s. Không đặt gì thì y như
     * trước, nên mọi cổng đang có không đổi hành vi. Luật nhận/loại giá trị ở `configs/apiTimeout.js`
     * — tóm tắt: `0` và `Infinity` bị TỪ CHỐI vì với axios chúng nghĩa là chờ mãi mãi.
     */
    const timeout = timeoutFromEnv(options.timeout, API_TIMEOUT_MS)

    const instance = Axios.create({
        baseURL: baseUrlValidated,
        timeout,
        headers,
    })

    const _changeBaseURL = (baseURL) => {
        instance.defaults.baseURL = baseURL
    }

    const _getBaseURL = () => {
        return instance.defaults.baseURL
    }

    const _setDefault = (property, value = null) => {
        instance.defaults[property] = value
    }

    instance.interceptors.response.use(
        response => response,
        error => {
            const { response } = error

            if (response && typeof response === 'object') {
                const { status, data } = response

                /*
                 * Blob KHÔNG được trải phẳng.
                 *
                 * Với `responseType: 'blob'` (các endpoint xuất file), axios trả `response.data` là
                 * một Blob kể cả khi status là 4xx. `typeof Blob === 'object'` nên nó lọt vào nhánh
                 * dưới, mà `{...blob}` cho ra `{}` — `size`/`type` là accessor trên prototype, không
                 * phải own enumerable property. Kết quả: mã lỗi nghiệp vụ biến mất, `errorCode()`
                 * luôn `null`, và mọi nhánh xử lý theo mã (ví dụ `M18_E003` — vượt giới hạn dòng khi
                 * xuất) thành code chết.
                 *
                 * Ném nguyên `AxiosError` để thân Blob còn nguyên tới tầng API. ⚠️ Riêng việc
                 * này CHƯA đủ: `bodyOf(error)` sẽ trả chính cái Blob, mà Blob không có `.code` nên
                 * `errorCode()` vẫn `null`. Phải `await blob.text()` rồi `JSON.parse` — làm ở
                 * `unwrapBlobError` trong `api/apiProductionPlanning.js`, vì `blob.text()` là async
                 * còn interceptor này thì đồng bộ.
                 *
                 * Chỉ ảnh hưởng response blob — đã rà toàn `src/`: `responseType: 'blob'` chỉ xuất
                 * hiện ở `api/apiProductionPlanning.js`.
                 */
                if (typeof Blob !== 'undefined' && data instanceof Blob) {
                    /*
                     * Gắn cờ để `_makeAuthRequest` / `_makeBasicRequest` KHÔNG bắn `errorSubscribers`.
                     *
                     * Guard trải phẳng ở dưới là `if (data && typeof data === 'object')`, nên lỗi
                     * thân JSON mất `.response` và hai hàm kia thoát sớm ở `if (!response)` —
                     * subscriber không chạy. Nhánh blob này giữ nguyên `AxiosError`, nên nếu không
                     * gắn cờ thì một cú 403 "thiếu quyền" khi bấm Xuất Excel sẽ đánh thức subscriber
                     * của `EnsureLoggedIn` (bắt `403 || 401`) và ĐĂNG XUẤT người dùng thay vì hiện
                     * thông báo lỗi.
                     *
                     * ⚠️ Đừng đọc câu trên thành "blob là lối DUY NHẤT còn đánh thức subscriber".
                     *    Thân **chuỗi / rỗng / `undefined`** (401 của gateway, HTML của proxy) cũng
                     *    không lọt vào guard `typeof data === 'object'`, rơi xuống `return
                     *    Promise.reject(error)` ở cuối và VẪN giữ `.response` ⇒ vẫn bắn subscriber.
                     *    Đo ở vòng review 14: đường subscriber CHỈ còn sống cho thân không-object,
                     *    trên cả `master` lẫn nhánh này. Dọn nó đi vì tưởng là code chết là giết một
                     *    lối tự đăng xuất đang chạy.
                     *
                     *    (Không phải lối DUY NHẤT. Lệnh sau liệt kê các lối không qua subscriber —
                     *    hai hit trong `EnsureLoggedIn` nằm trong `subscribeErrorRequest` nên bị loại.
                     *    ⚠️ Bộ loại trừ ghim theo SỐ DÒNG của `EnsureLoggedIn.js`, đúng tại thời điểm
                     *    viết; sửa file đó là lệnh trôi. Nếu số hit ≠ 5 thì kiểm tay bằng
                     *    `grep -n 'subscribeErrorRequest\|_handleRefreshToken\|logoutUser(' src/share/EnsureLoggedIn.js`:
                     *      grep -rn 'logoutUser(' src/ | grep -v ':[0-9]*: *\*' \
                     *        | grep -vE 'EnsureLoggedIn.js:(3[0-9]|5[0-9]):'
                     *    Các lối
                     *    KHÔNG qua `errorSubscribers`: `EnsureLoggedIn._handleRefreshToken` (2 chỗ,
                     *    tự đăng xuất khi refresh token hỏng), `AbleToPrint/ableToPrint.js` (401),
                     *    và `Sidebar/Sidebar.js` (2 chỗ, nút Logout thủ công).)
                     *
                     * Cờ này chỉ gắn trên nhánh blob nên không đụng tới lối đó.
                     */
                    error.__skipErrorSubscribers = true

                    return Promise.reject(error)
                }

                if (data && typeof data === 'object') {
                    return Promise.reject({
                        ...data,
                        status: status,
                        message: data.message || error.message
                    })
                }
            }
            return Promise.reject(error)
        }
    )

    return {
        setDefault: _setDefault,
        getBaseURL: _getBaseURL,
        changeBaseURL: _changeBaseURL,
        makeRequest: _makeRequest(instance),
        makeBasicAuthRequest: _makeBasicRequest(instance),
        makeAuthRequest: _makeAuthRequest(instance),
        subscribeErrorRequest: _subscribeErrorRequest(instance),
    }
}
